import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Eye, Plus, Ban, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ACard, AH1, ABtn, Status, fmtDate } from "./ui";

const StudentDialog = ({ userId, onClose, onChanged }) => {
  const [d, setD] = useState(null);
  const [courses, setCourses] = useState([]);
  const [pick, setPick] = useState("");
  const load = () => api.get(`/admin/students/${userId}`).then((r) => setD(r.data));
  useEffect(() => {
    if (!userId) return;
    setD(null);
    load();
    api.get("/admin/courses").then((r) => setCourses(r.data));
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const grant = async () => {
    if (!pick) return;
    try { await api.post("/admin/enrollments", { user_id: userId, course_id: pick }); toast.success("تم منح الوصول"); load(); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (cid) => {
    if (!window.confirm("سحب وصول الطالب لهذا الكورس؟")) return;
    await api.post("/admin/enrollments/revoke", { user_id: userId, course_id: cid }); toast.success("تم سحب الوصول"); load(); onChanged();
  };
  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[var(--kh-ws-card)] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="student-dialog">
        <DialogHeader><DialogTitle className="text-right">{d?.user.name}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-5">
            <p className="text-sm text-slate-400" dir="ltr">{d.user.email} {d.user.phone && `• ${d.user.phone}`}</p>
            <div className="flex gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="grant-course-select"
                className="flex-1 bg-[var(--kh-ws)] border border-white/10 rounded-xl px-3 py-2 text-sm">
                <option value="">اختار كورس لمنح الوصول...</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <ABtn onClick={grant} data-testid="grant-access-btn"><Plus size={15} />منح</ABtn>
            </div>
            <div className="space-y-2">
              {d.enrollments.length === 0 && <p className="text-sm text-slate-500">لا توجد اشتراكات.</p>}
              {d.enrollments.map((e) => (
                <div key={e.course_id} className="rounded-xl bg-white/[0.03] p-3 space-y-2" data-testid={`student-enrollment-${e.course_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm">{e.course_title}</span>
                    <span className="flex items-center gap-2"><Status s={e.status} />
                      {e.status === "active" && <ABtn variant="danger" onClick={() => revoke(e.course_id)} data-testid={`revoke-access-${e.course_id}`}>سحب</ABtn>}
                    </span>
                  </div>
                  <div className="flex items-center gap-3"><Progress value={e.percent} className="h-1.5 bg-white/10" /><span className="text-xs text-slate-400 whitespace-nowrap">{e.completed}/{e.total_lessons}</span></div>
                  <p className="text-[11px] text-slate-500">آخر نشاط: {fmtDate(e.last_activity)}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function AdminStudents() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get(`/admin/students?q=${encodeURIComponent(q)}`).then((r) => setList(r.data));
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleActive = async (u) => {
    await api.patch(`/admin/students/${u.id}`, { is_active: !u.is_active });
    toast.success(u.is_active ? "تم إيقاف الحساب" : "تم تفعيل الحساب");
    load();
  };

  return (
    <div data-testid="admin-students">
      <AH1>الطلاب</AH1>
      <label className="relative block max-w-sm mb-6">
        <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو البريد أو الموبايل" data-testid="students-search-input"
          className="w-full bg-[var(--kh-ws-card)] border border-white/10 rounded-xl py-2.5 pr-9 pl-3 text-sm focus:outline-none focus:border-[var(--kh-violet)]" />
      </label>
      <div className="space-y-2">
        {list.length === 0 && <p className="text-slate-500">لا يوجد طلاب.</p>}
        {list.map((u) => (
          <ACard key={u.id} className="flex flex-wrap items-center gap-4 !py-4" data-testid={`student-row-${u.id}`}>
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium">{u.name} {!u.is_active && <span className="text-xs text-red-300">(موقوف)</span>}</p>
              <p className="text-xs text-slate-400" dir="ltr">{u.email}{u.phone && ` • ${u.phone}`}</p>
            </div>
            <span className="text-xs text-slate-400">{u.enrollments_count} كورس • انضم {fmtDate(u.created_at)}</span>
            <ABtn variant="ghost" onClick={() => setOpen(u.id)} data-testid={`view-student-${u.id}`}><Eye size={15} />التفاصيل</ABtn>
            <ABtn variant={u.is_active ? "danger" : "success"} onClick={() => toggleActive(u)} data-testid={`toggle-student-${u.id}`}>
              {u.is_active ? <><Ban size={15} />إيقاف</> : <><CheckCircle2 size={15} />تفعيل</>}
            </ABtn>
          </ACard>
        ))}
      </div>
      <StudentDialog userId={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
